import { Button, Heading, HFlow, Icon, Text, Tooltip, VFlow } from 'bold-ui'
import { usePecField } from 'components/form'
import { confirm } from 'components/modals/confirm'
import { ButtonLink } from 'components/route'
import { NavigationButton } from 'components/route/NavigationButton'
import { useFlags } from 'config/useFlagsContext'
import { MChatRiscoEnum } from 'graphql/types.generated'
import { isEmpty } from 'lodash'
import React from 'react'
import { MetaPath } from 'util/metaPath'

import { MChatRfAccordionSection, MChatRfAccordionSectionProps } from '../../components/MChatRfAccordionSection'
import { MChatRfAccordionStepper } from '../../components/MChatRfAccordionStepper'
import { MChatAccessDocLink } from '../../mchat/components/MChatAccessDocLink'
import { MChatRfAccordionSectionEnum } from '../../model-mChatRf'
import { ConsultaSeguimentoFieldModel } from '../model-consultaSeguimento'

interface ConsultaSeguimentoFieldAccordionSectionProps
  extends Omit<MChatRfAccordionSectionProps, 'section' | 'value' | 'stepper' | 'primaryActions' | 'secondaryActions'> {
  name: MetaPath<ConsultaSeguimentoFieldModel>
  valuesAtendimentoAnterior: ConsultaSeguimentoFieldModel
  mChatAtualRisco: MChatRiscoEnum
  mChatAnteriorRisco: MChatRiscoEnum
  modalUrl: string
  aplicarButtonRef: React.RefObject<HTMLButtonElement>
  onDelete: () => void
}

export function ConsultaSeguimentoFieldAccordionSection(props: Readonly<ConsultaSeguimentoFieldAccordionSectionProps>) {
  const {
    name,
    valuesAtendimentoAnterior,
    modalUrl,
    mChatAtualRisco,
    mChatAnteriorRisco,
    aplicarButtonRef,
    onDelete,
    ...sectionProps
  } = props

  const { M_CHAT_RF_ENABLED } = useFlags()

  const {
    input: { value },
  } = usePecField({ name, subscription: { value: true } })

  const hasDadosAtendimentoAtual = !isEmpty(value)
  const hasDadosAtendimentoAnterior = !isEmpty(valuesAtendimentoAnterior)

  const isMChatAtualRiscoMedio = mChatAtualRisco === MChatRiscoEnum.MEDIO
  const isMChatAnteriorRiscoMedio = mChatAnteriorRisco === MChatRiscoEnum.MEDIO
  const isMChatAnteriorRiscoElevado = mChatAnteriorRisco === MChatRiscoEnum.ELEVADO

  const isConsultaSeguimentoAtiva = isMChatAtualRiscoMedio || isMChatAnteriorRiscoMedio
  const isReavaliar = hasDadosAtendimentoAnterior && !hasDadosAtendimentoAtual
  const hideReavaliar = isReavaliar && isMChatAnteriorRiscoElevado

  const handleDelete = () => confirmDeleteDialog(onDelete)

  return (
    <MChatRfAccordionSection
      section={MChatRfAccordionSectionEnum.CONSULTA_SEGUIMENTO}
      value={hasDadosAtendimentoAtual ? value : valuesAtendimentoAnterior}
      isConsultaSeguimentoAtiva={isConsultaSeguimentoAtiva}
      stepper={
        <MChatRfAccordionStepper
          showPendingIcon={!isConsultaSeguimentoAtiva}
          showCheckIcon={false}
          showConnector={false}
          showExclamationIcon={isConsultaSeguimentoAtiva}
        />
      }
      primaryActions={
        !M_CHAT_RF_ENABLED ? (
          <MChatAccessDocLink />
        ) : (
          !hasDadosAtendimentoAtual &&
          !hideReavaliar && (
            <NavigationButton
              kind={isConsultaSeguimentoAtiva && !isReavaliar ? 'primary' : 'normal'}
              size='small'
              to={modalUrl}
              innerRef={aplicarButtonRef}
            >
              {isReavaliar ? 'Reavaliar' : 'Avaliar'}
            </NavigationButton>
          )
        )
      }
      secondaryActions={
        <HFlow hSpacing={0.5}>
          {(hasDadosAtendimentoAnterior || hasDadosAtendimentoAtual) && (
            <Tooltip text='Imprimir'>
              <Button type='button' kind='normal' skin='ghost' size='small'>
                <Icon icon='printerOutline' />
              </Button>
            </Tooltip>
          )}
          {hasDadosAtendimentoAtual && (
            <HFlow hSpacing={0.5}>
              <Tooltip text='Editar'>
                <ButtonLink
                  type='button'
                  kind='normal'
                  skin='ghost'
                  size='small'
                  disabled={!hasDadosAtendimentoAtual}
                  to={`${modalUrl}/editar`}
                >
                  <Icon icon='penOutline' />
                </ButtonLink>
              </Tooltip>
              <Tooltip text='Excluir'>
                <Button type='button' kind='normal' skin='ghost' size='small' onClick={handleDelete}>
                  <Icon icon='trashOutline' />
                </Button>
              </Tooltip>
            </HFlow>
          )}
        </HFlow>
      }
      hasCurrentValue={!!value}
      {...sectionProps}
    />
  )
}

const confirmDeleteDialog = (handleConfirm: () => void) => {
  confirm({
    confirmLabel: 'Excluir',
    cancelLabel: 'Cancelar',
    type: 'danger',
    size: 'auto',
    onConfirm: handleConfirm,
    render: () => (
      <HFlow>
        <Icon icon='exclamationTriangleOutline' fill='danger' size={3} />
        <VFlow vSpacing={0.125}>
          <Heading level={1}>Deseja excluir a avaliação?</Heading>
          <Text>As informações registradas e o resultado do M-CHAT-R™ serão perdidos.</Text>
        </VFlow>
      </HFlow>
    ),
  })()
}
